import { createSlice } from '@reduxjs/toolkit'
import { format } from 'date-fns'
import { studiesTexts } from 'locale/es'
import StudyService from 'services/StudyService'
import { toast } from './notificationReducer'

const questionTypes = {
  0: "INFO",
  1: "FREE TEXT",
  2: "RANGE",
  3: "DATE TIME",
  4: "SINGLE SELECT",
  5: "NUMERIC",
  6: "MULTIPLE SELECT",
  7: "CALCULATION"
}

const commonHeaders = [
  {
    id: "id",
    label: "ID",
    isSortable: false
  },
  {
    id: "participant",
    label: "Participant",
    isSortable: false
  },
  {
    id: "submitted_at",
    label: "Data",
    isSortable: true
  },
  {
    id: "user",
    label: "Usuari",
    isSortable: false
  }
]

const initialState = {
  data: {
    questions: commonHeaders,
    answers: [],
  },
  loading: false,
  error: null,
}

function mappedAnswers(data, uniqueQuestions) {
  return data.reduce((allData, currentDataPoint) => {
    // * If the user didn't replied to all answers, add them to the data point to prevent columns order bugs and to display - in the cell
    const missingResponses = uniqueQuestions.filter(question => {
      const { name } = question
      const { answers } = currentDataPoint

      const answer = answers.find(answer => {
        if (!answer.question) return true

        return answer.question?.variable_name === name
      })
      return !answer
    })

    if (missingResponses.length > 0) {
      const missingResponsesWithEmptyValues = missingResponses.map(missingResponse => {
        return {
          question: {
            variable_name: missingResponse.name,
            section_id: missingResponse.sectionId,
            order: missingResponse.order,
            type: null
          },
          answer: "-",
          label: "-",
          value: "-"
        }
      })

      currentDataPoint = { ...currentDataPoint, answers: [...currentDataPoint.answers, ...missingResponsesWithEmptyValues] }
    }

    const formattedAnswers = currentDataPoint.answers.reduce((allAnswers, answer) => {
      const { question, data, data_label } = answer
      let answerData

      if (!question) return allAnswers

      if (data === data_label) {
        answerData = data
      }

      if (questionTypes[question.type]?.includes("SELECT")) {
        answerData = `[${data}] ${data_label}`
      }

      return [
        ...allAnswers,
        {
          answer: answerData,
          label: data_label,
          value: data,
          variable_name: question.variable_name,
          sectionId: question.section_id,
          order: question.order
        }
      ]
    }, []).filter(x => x.variable_name)

    const sortedAnswers = [...formattedAnswers]
      .sort((a, b) => {
        if (a.sectionId !== b.sectionId) {
          return a.sectionId - b.sectionId;
        }
        return a.order - b.order;
      })
      .reduce((result, question) => {
        return {
          ...result, [question.variable_name]: {
            answer: question.answer || "-",
            label: question.label || "-",
            value: question.value || "-",
          },
        }
      }, {});

    const extraAnswerFields1 = {
      id: {
        answer: currentDataPoint.id,
      },
      participant: {
        answer: !!currentDataPoint.participant ? currentDataPoint.participant.uuid : "Anònim"
      },
    }

    const extraAnswerFields2 = {
      submitted_at: {
        answer: format(new Date(currentDataPoint.updated_at), "dd-MM-yyyy HH:mm")
      },
      user: {
        answer: !!currentDataPoint.user ? currentDataPoint.user.username : "Anònim"
      },
    }

    const finalAnswers = { ...extraAnswerFields1, ...sortedAnswers, ...extraAnswerFields2 }

    return [...allData, finalAnswers]
  }, [])
}

const studyFormAnswersExportSlice = createSlice({
  name: 'studyFormAnswersExport',
  initialState,
  reducers: {
    setExportAnswers(state, action) {
      const { data } = action.payload

      if (!data) {
        return initialState
      }

      const insertIndex = 2; // Insert the new headers after the 'participant' header

      const tableHeaders = [
        ...commonHeaders.slice(0, insertIndex),
        ...data.questions,
        ...commonHeaders.slice(insertIndex)
      ];

      return { ...state, data: { questions: tableHeaders, answers: mappedAnswers(data.answers, data.questions) }, loading: false, error: null }
    },
    setAnswersLoading(state) {
      state.loading = true;
      state.error = null;
    },
    setAnswersError(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    resetFormAnswersState(state, action) {
      return initialState
    }
  },
})

export const { setExportAnswers, setAnswersLoading, setAnswersError, resetFormAnswersState } =
  studyFormAnswersExportSlice.actions

export const initializeFormAnswersExport = (studyId, formId) => {
  return async (dispatch, getState) => {
    try {
      dispatch(setAnswersLoading())

      const { data: answersData } = await StudyService.getAllFormAnswers(formId)
      // const { data: questionsData } = await StudyService.getQuestions(studyId, formId)

      const questionsFromTable = getState().studyFormAnswers.data.questions

      dispatch(setExportAnswers({ data: { questions: questionsFromTable, answers: answersData.data.item } }))
    }
    catch (error) {
      console.error(error)
      dispatch(setAnswersError({ message: error.message, status: error.response?.status }));
      dispatch(toast(studiesTexts.ANSWERS_ERROR, 'error'))
    }
  }
}

export default studyFormAnswersExportSlice.reducer